import { Controller } from "@hotwired/stimulus";

const MAX_HISTORY = 6;       // last 3 turns (user + assistant pairs)
const SESSION_KEY = "docs-chat-v1";

export default class extends Controller {
  static targets = ["input", "messages", "send", "toggle", "panel", "starters", "counter", "chatWrapper"];

  connect() {
    this._abortController = null;
    this._loading = false;
    this._history = [];
    this._sessionMessages = []; // {role, text}[] for sessionStorage
    this._restoreSession();
    this._updateCounter();
  }

  disconnect() {
    this._abortController?.abort();
  }

  togglePanel() {
    const isLarge = window.innerWidth >= 768;
    const wrapper = this.chatWrapperTarget;

    if (isLarge) {
      const isOpen = wrapper.classList.toggle("chat-side-open");
      this.element.classList.toggle("chat-side-open", isOpen);
      this.toggleTargets.forEach(t => t.setAttribute("aria-expanded", String(isOpen)));
      if (isOpen) this.inputTarget.focus();
    } else {
      const panel = this.panelTarget;
      const isOpen = panel.classList.toggle("chat-open");
      this.toggleTargets.forEach(t => t.setAttribute("aria-expanded", String(isOpen)));
      if (isOpen) this.inputTarget.focus();
    }
  }

  // ── Starter questions ─────────────────────────────────────────────────────

  sendStarter(event) {
    const question = event.currentTarget.dataset.question;
    if (!question) return;
    this.inputTarget.value = question;
    this._updateCounter();
    this._resizeInput();
    this.send();
  }

  // ── Input events ──────────────────────────────────────────────────────────

  onInput() {
    this._updateCounter();
    this._resizeInput();
  }

  _updateCounter() {
    const len = this.inputTarget.value.length;
    this.counterTarget.textContent = `${len} / 500`;
    this.counterTarget.classList.toggle("docs-chat-counter--warn", len > 449);
  }

  _resizeInput() {
    const ta = this.inputTarget;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
  }

  // ── Send / stream ─────────────────────────────────────────────────────────

  async send() {
    const question = this.inputTarget.value.trim();
    if (!question || this._loading) return;

    const historySnapshot = this._history.slice(-MAX_HISTORY);

    this._hideStarters();
    this._appendMessage("user", question);
    this.inputTarget.value = "";
    this._updateCounter();
    this._resizeInput();

    const assistantEl = this._appendMessage("assistant", "");
    const typingEl = document.createElement("span");
    typingEl.className = "chat-typing";
    typingEl.textContent = "…";
    assistantEl.appendChild(typingEl);

    this._loading = true;
    this.sendTarget.disabled = true;

    this._abortController = new AbortController();
    let assistantText = "";

    try {
      const response = await fetch(this.element.dataset.chatUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, history: historySnapshot }),
        signal: this._abortController.signal,
      });

      if (!response.ok) {
        typingEl.remove();
        assistantEl.textContent =
          response.status === 429
            ? "Too many requests. Please wait a moment."
            : "Something went wrong. Please try again.";
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let started = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") break;

          try {
            const text = JSON.parse(payload);
            if (!started) {
              typingEl.remove();
              started = true;
            }
            assistantText += text;
            // Show plain text while streaming for performance
            assistantEl.textContent = assistantText;
            this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
          } catch {
            /* ignore parse errors */
          }
        }
      }
      if (!started) typingEl.remove();

      // Render markdown and add copy button once the full response is in
      if (assistantText) {
        this._renderMarkdown(assistantEl, assistantText);
        this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;

        this._history.push({ role: "user", content: question });
        this._history.push({ role: "assistant", content: assistantText });
        if (this._history.length > MAX_HISTORY) {
          this._history = this._history.slice(-MAX_HISTORY);
        }

        this._sessionMessages.push({ role: "user", text: question });
        this._sessionMessages.push({ role: "assistant", text: assistantText });
        this._saveSession();
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        typingEl.remove();
        assistantEl.textContent = "Connection error. Please try again.";
      }
    } finally {
      this._loading = false;
      this.sendTarget.disabled = false;
      this.inputTarget.focus();
    }
  }

  keydown(event) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.send();
    }
  }

  // ── Clear chat ────────────────────────────────────────────────────────────

  clearChat() {
    // Remove all message elements, keeping the starters target in place
    const toRemove = Array.from(this.messagesTarget.children).filter(
      (el) => el !== this.startersTarget
    );
    for (const el of toRemove) el.remove();
    this._history = [];
    this._sessionMessages = [];
    this.startersTarget.hidden = false;
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
  }

  // ── Session persistence ───────────────────────────────────────────────────

  _saveSession() {
    try {
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ history: this._history, messages: this._sessionMessages })
      );
    } catch {}
  }

  _restoreSession() {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return;
      const { history, messages } = JSON.parse(raw);
      if (Array.isArray(history)) this._history = history;
      if (Array.isArray(messages) && messages.length > 0) {
        this._sessionMessages = messages;
        this._hideStarters();
        for (const { role, text } of messages) {
          const el = this._appendMessage(role, text);
          if (role === "assistant") this._renderMarkdown(el, text);
        }
      }
    } catch {}
  }

  // ── Starters ──────────────────────────────────────────────────────────────

  _hideStarters() {
    this.startersTarget.hidden = true;
  }

  // ── Markdown renderer ─────────────────────────────────────────────────────
  // Builds DOM nodes from text — never uses innerHTML with user-sourced strings.

  _renderMarkdown(el, text) {
    // Clear existing content
    while (el.firstChild) el.firstChild.remove();

    // Auto-close unclosed fenced code block (odd count means truncated response)
    const fenceCount = (text.match(/```/g) || []).length;
    if (fenceCount % 2 !== 0) text += "\n```";

    const frag = document.createDocumentFragment();
    const codeBlockRe = /```(\w*)\n?([\s\S]*?)```/g;
    let lastIndex = 0;
    let match;

    while ((match = codeBlockRe.exec(text)) !== null) {
      if (match.index > lastIndex) {
        this._appendInline(frag, text.slice(lastIndex, match.index));
      }
      const wrap = document.createElement("div");
      wrap.className = "chat-code-wrap";
      const pre = document.createElement("pre");
      const code = document.createElement("code");
      const codeText = match[2].replace(/\n$/, "");
      code.textContent = codeText;
      pre.appendChild(code);
      wrap.appendChild(pre);
      const copyCode = document.createElement("button");
      copyCode.className = "chat-code-copy";
      copyCode.textContent = "Copy";
      copyCode.setAttribute("aria-label", "Copy code");
      copyCode.addEventListener("click", () => {
        navigator.clipboard.writeText(codeText).catch(() => {});
        copyCode.textContent = "Copied!";
        setTimeout(() => { copyCode.textContent = "Copy"; }, 1500);
      });
      wrap.appendChild(copyCode);
      frag.appendChild(wrap);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      this._appendInline(frag, text.slice(lastIndex));
    }

    // Copy button
    const copyBtn = document.createElement("button");
    copyBtn.className = "chat-message-copy";
    copyBtn.setAttribute("aria-label", "Copy response");
    copyBtn.setAttribute("title", "Copy");
    copyBtn.innerHTML =
      '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
    const rawText = text;
    copyBtn.addEventListener("click", () => {
      navigator.clipboard.writeText(rawText).catch(() => {});
      copyBtn.classList.add("chat-message-copy--copied");
      copyBtn.setAttribute("aria-label", "Copied!");
      setTimeout(() => {
        copyBtn.classList.remove("chat-message-copy--copied");
        copyBtn.setAttribute("aria-label", "Copy response");
      }, 1500);
    });
    frag.appendChild(copyBtn);

    el.appendChild(frag);
  }

  _appendInline(parent, text) {
    const lines = text.split("\n");
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (i > 0) parent.appendChild(document.createElement("br"));

      // Heading
      const headingMatch = line.match(/^#{1,3}\s+(.*)/);
      if (headingMatch) {
        const strong = document.createElement("strong");
        strong.className = "chat-heading";
        this._appendFormatted(strong, headingMatch[1]);
        parent.appendChild(strong);
        continue;
      }

      // Unordered list item
      const ulMatch = line.match(/^[-*]\s+(.*)/);
      if (ulMatch) {
        const span = document.createElement("span");
        span.appendChild(document.createTextNode("• "));
        this._appendFormatted(span, ulMatch[1]);
        parent.appendChild(span);
        continue;
      }

      // Ordered list item
      const olMatch = line.match(/^(\d+)\.\s+(.*)/);
      if (olMatch) {
        const span = document.createElement("span");
        span.appendChild(document.createTextNode(olMatch[1] + ". "));
        this._appendFormatted(span, olMatch[2]);
        parent.appendChild(span);
        continue;
      }

      this._appendFormatted(parent, line);
    }
  }

  _appendFormatted(parent, text) {
    // Handle inline `code`, **bold**, *italic*, [text](url), bare https:// URLs — all via textContent/href, no innerHTML
    const re = /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s<>")\]]+|`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let last = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) {
        parent.appendChild(document.createTextNode(text.slice(last, m.index)));
      }
      const chunk = m[0];
      if (chunk.startsWith("[")) {
        const a = document.createElement("a");
        a.href = m[3];
        a.textContent = m[2];
        a.className = "cs-chat-link";
        parent.appendChild(a);
      } else if (chunk.startsWith("http")) {
        const a = document.createElement("a");
        a.href = chunk;
        a.textContent = chunk;
        a.className = "cs-chat-link";
        parent.appendChild(a);
      } else if (chunk.startsWith("`")) {
        const code = document.createElement("code");
        code.textContent = chunk.slice(1, -1);
        parent.appendChild(code);
      } else if (chunk.startsWith("**")) {
        const strong = document.createElement("strong");
        strong.textContent = chunk.slice(2, -2);
        parent.appendChild(strong);
      } else {
        const em = document.createElement("em");
        em.textContent = chunk.slice(1, -1);
        parent.appendChild(em);
      }
      last = m.index + chunk.length;
    }
    if (last < text.length) {
      parent.appendChild(document.createTextNode(text.slice(last)));
    }
  }

  // ── Message factory ───────────────────────────────────────────────────────

  _appendMessage(role, text) {
    const el = document.createElement("div");
    el.className = `chat-message chat-message--${role}`;
    el.textContent = text;
    this.messagesTarget.appendChild(el);
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
    return el;
  }
}
